import {
    Component,
    Element,
    Event,
    h,
    Host,
    Method,
    Prop,
    State,
} from "@stencil/core";
import type { EventEmitter } from "@stencil/core";

import {
    ConversationLink,
    LinkSuggestion,
    Suggestions,
} from "../../utils/backend/reply-parsed";

import type {
    AnswerBy,
    Conversation,
} from "../../utils/backend/reply-parsed";
import { Backend, MessageParams } from "../../utils/backend/backend";
import { uuid } from "../../utils/uuid";
import { preloadImages } from "../../utils/preload-images";
import disableBot from "../../disable-bot";
import store from "./store";
import { trapFocus } from "../../utils/trap-focus/trap-focus";
import { mesosConfig } from "../../config/mesos-config";
import { LS_FREE_TEXT } from "../../config/feature-buttons";
import type { BotId } from "../../global/models/models";

@Component({
    tag: "leichte-sprache-bot",
    styleUrl: "leichte-sprache-bot.scss",
    shadow: true,
})
export class LeichteSpracheBot {
    // Public API
    @Prop() headerText = "Chat-Bot Leichte Sprache";
    @Prop() botID = "LEICHTS" as BotId;
    @Prop() jwt: string;
    @Prop() clientID: string;
    @Method() async open() {
        store.openChatbot();
    }
    @Method() async close() {
        store.closeChatbot();
    }
    @Method() async restart() {
        this.conversation = [];
        this.answerBy = undefined;
        this.conversationId = null;
        this.chatbotRestarted.emit();
        this.startConversation();
    }
    @Event({
        eventName: "chatbot-open",
        composed: true,
        cancelable: true,
        bubbles: true,
    })
    chatbotOpenend: EventEmitter;
    @Event({
        eventName: "chatbot-close",
        composed: true,
        cancelable: true,
        bubbles: true,
    })
    chatbotClosed: EventEmitter;
    @Event({
        eventName: "chatbot-restart",
        composed: true,
        cancelable: true,
        bubbles: true,
    })
    chatbotRestarted: EventEmitter;

    // Private API
    backend = new Backend();
    preloadImages = preloadImages;
    sessionId = uuid();
    conversationId: string | null = null;

    @State() hidden = true;
    @State() answerBy: AnswerBy;
    @State() conversation: Conversation = [];
    @State() loading: boolean;
    @State() error: boolean;
    @Element() hostElement: HTMLElement;
    trapFocus: (e: KeyboardEvent) => void;

    constructor() {
        // For some reason event handlers are called twice for free-text and salary-input
        // (although the event emitter inside the component is only called once).
        // I want to move the Bot class to the store anyways, so the salary-input
        // can just make the backend requests itself
        this.closeChatbotOnEsc = this.closeChatbotOnEsc.bind(this);
        this.trapFocus = trapFocus(this.hostElement);
        this.onComponentLoad();
    }

    onComponentLoad() {
        document.addEventListener('appload', () => alert("Test LS document.appload"));
    }

    connectedCallback() {
        store.onOpen(() => {
            document.addEventListener("keydown", this.closeChatbotOnEsc);
            document.addEventListener("keydown", this.trapFocus);
            this.chatbotOpenend.emit();
            this.hidden = false;
            this.preventBodyScroll();
            if (this.conversation.length === 0) {
                this.startConversation();
            }
        });
        store.onClose(() => {
            document.removeEventListener("keydown", this.closeChatbotOnEsc);
            document.removeEventListener("keydown", this.trapFocus);
            this.chatbotClosed.emit();
            this.hidden = true;
            this.allowBodyScroll();
        });
    }

    disconnectedCallback() {
        document.removeEventListener("keydown", this.closeChatbotOnEsc);
        document.removeEventListener("keydown", this.trapFocus);
    }

    preventBodyScroll() {
        document.body.style.overflow = "hidden";
    }

    allowBodyScroll() {
        document.body.style.overflow = "auto";
    }

    closeChatbotOnEsc(e: any) {
        if (e.key === "Escape") {
            if (this.isFocusInsideBot()) {
                this.close();
            }
        }
    }

    isFocusInsideBot() {
        return this.hostElement.contains(document.activeElement);
    }

    get showErrorScreen() {
        return Boolean(this.error);
    }

    handleClose() {
        this.close();
    }

    startConversation() {
        this.conversationId = uuid();
        const intent = LS_FREE_TEXT.message;
        this.sendMessage({
            message: intent,
            sessionId: this.sessionId,
            conversationId: this.conversationId,
            botId: this.botID,
            clientID: this.clientID,
        });
    }

    async sendMessage(messageParams: MessageParams) {
        try {
            this.loading = true;
            const { messages, answerBy } = await this.backend.message(
                messageParams
            );
            await this.preloadImages(messages);
            this.loading = false;
            this.answerBy = answerBy;
            this.addToConversation(...messages);
        } catch (e) {
            this.error = e;
        }
    }

    addSuggestionLinksToConversation() {
        const links: LinkSuggestion[] = (
            this.answerBy as Suggestions
        ).elements.filter(
            (element): element is LinkSuggestion => element.type === "link"
        );
        const conversationLinks = links.map((link) => {
            return {
                type: "conversationLink",
                title: link.title,
                screenReader: link.screenReader,
                url: link.url,
            } as ConversationLink;
        });
        this.addToConversation(...conversationLinks);
    }

    handleFreeText(e: CustomEvent) {
        const freeText = e.detail;
        if (!freeText) {
            return;
        }
        if (this.answerBy?.type === "suggestions") {
            this.addSuggestionLinksToConversation();
        }
        this.addToConversation({
            type: "userMessage",
            text: freeText.replace(/</g, "&lt;").replace(/>/g, "&gt;"),
        });
        this.sendMessage({
            message: freeText,
            sessionId: this.sessionId,
            conversationId: this.conversationId as string,
            botId: this.botID,
            clientID: this.clientID,
        });
    }

    handleChoiceSelected(e: any) {
        const { title, payload } = e.detail;
        this.addToConversation({ type: "userMessage", text: title });
        this.sendMessage({
            message: String(payload),
            sessionId: this.sessionId,
            conversationId: this.conversationId as string,
            botId: this.botID,
            clientID: this.clientID,
        });
    }

    handleLinkSuggestionSelected(e: CustomEvent) {
        const link: LinkSuggestion = e.detail;
        // If the are button and link suggestions mixed and a link with no payload is clicked, the conversation will not progress and only the link opens
        if (!link.payload) {
            return;
        }
        // If a link has a payload it means the backend wants to answer with new messages. There might be more than one link and we want to
        // be able to click them later, so we will add them to the conversation, while removing any buttons.
        this.addSuggestionLinksToConversation();

        this.sendMessage({
            message: String(link.payload),
            sessionId: this.sessionId,
            conversationId: this.conversationId as string,
            botId: this.botID,
            clientID: this.clientID,
        });
    }

    handleButtonSuggestionSelected(e: CustomEvent) {
        // If we have mixed button and link suggestions we want to keep all links, but only the selected button
        this.addSuggestionLinksToConversation();

        const { title, payload } = e.detail;
        this.addToConversation({ type: "userMessage", text: title });
        this.sendMessage({
            message: String(payload),
            sessionId: this.sessionId,
            conversationId: this.conversationId as string,
            botId: this.botID,
            clientID: this.clientID,
        });
    }

    addToConversation(...newConversation: Conversation) {
        this.conversation = [...this.conversation, ...newConversation];
    }

    getInput() {
        if (!this.answerBy) {
            return (
                <leichte-sprache-free-text
                    disabled={true}
                    onFreeText={this.handleFreeText.bind(this)}
                ></leichte-sprache-free-text>
            );
        }
        switch (this.answerBy.type) {
            case "multipleChoice":
                return (
                    <leichte-sprache-free-text
                        disabled={true}
                        onFreeText={this.handleFreeText.bind(this)}
                    ></leichte-sprache-free-text>
                );
            case "textInput":
            case "suggestions":
                return (
                    <leichte-sprache-free-text
                        onFreeText={this.handleFreeText.bind(this)}
                    ></leichte-sprache-free-text>
                );
        }
    }

    render() {
        return (
            <Host
                hidden={disableBot(
                    mesosConfig,
                    window.location.host,
                    window.location.pathname
                )}
                id="ls-digitaler-assistent"
                aria-label="Digitaler Assistent"
                role="dialog"
                aria-hidden={this.hidden ? "true" : "false"}
                class={this.hidden ? "hide-chatbot" : ""}
            >
                <leichte-sprache-header
                    headerText={this.headerText}
                    onRestart={this.restart.bind(this)}
                    onClose={this.handleClose.bind(this)}
                ></leichte-sprache-header>

                <leichte-sprache-conversation
                    conversation={this.conversation}
                    answerBy={this.answerBy}
                    loading={this.loading}
                    onChoiceSelected={this.handleChoiceSelected.bind(this)}
                    onLinkSuggestionSelected={this.handleLinkSuggestionSelected.bind(
                        this
                    )}
                    onButtonSuggestionSelected={this.handleButtonSuggestionSelected.bind(
                        this
                    )}
                ></leichte-sprache-conversation>

                {this.getInput()}
            </Host>
        );
    }
}
